import type { TranslationKeys } from '../types';

/**
 * Chinese (Simplified) translations
 * 简体中文翻译
 */
export const zhCN: TranslationKeys = {
  language: {
    cmn: '中文',
    eng: '英语',
    jpn: '日语',
    kor: '韩语',
    spa: '西班牙语',
    fra: '法语',
    deu: '德语',
    rus: '俄语',
    por: '葡萄牙语',
    ita: '意大利语',
    yue: '粤语',
    nld: '荷兰语',
    arb: '阿拉伯语',
    ind: '印尼语',
  },

  // 通用
  common: {
    loading: '加载中...',
    error: '错误',
    success: '成功',
    cancel: '取消',
    confirm: '确认',
    save: '保存',
    delete: '删除',
    edit: '编辑',
    close: '关闭',
    back: '返回',
    next: '下一步',
    previous: '上一步',
    retry: '重试',
    refresh: '刷新',
    saving: '保存中...',
    processing: '处理中...',
    understood: '我知道了',
    popular: '热门语言',
    tokenNotEnough: 'Token 余额不足，请购买更多 tokens 或升级订阅'
  },

  // 导航
  nav: {
    home: '首页',
    translation: '实时翻译',
    history: '对话历史',
    settings: '设置',
    help: '帮助',
    about: '关于',
    profile: '个人资料',
    logout: '退出登录',
  },

  // 翻译功能
  translation: {
    startTranslation: '开始同传',
    stopTranslation: '停止翻译',
    stopping: '正在停止...',
    realTimeTranslation: '实时翻译',
    sourceLanguage: '源语言',
    targetLanguage: '目标语言',
    swapLanguages: '交换语言',
    selectLanguage: '选择语言',
    selectLanguageDescription :'请选择两种你要互相翻译的语言',
    detectLanguage: '自动检测',
    translating: '翻译中...',
    translationComplete: '翻译完成',
    translationError: '翻译失败',
    noTranslationYet: '暂无翻译内容',
    speakNow: '请开始说话...',
    listening: '正在听取...',
    processing: '处理中...',
    sameLanguageError: '源语言和目标语言不能相同，请选择不同的语言',
  },

  // 音频/麦克风
  audio: {
    microphonePermission: '麦克风权限',
    microphonePermissionDenied: '麦克风权限被拒绝',
    selectMicrophone: '选择麦克风',
    noMicrophoneFound: '未找到麦克风设备',
    microphoneError: '麦克风错误',
    requestPermission: '请授权麦克风权限',
    permissionGranted: '权限已授予',
    permissionRequired: '请先授权麦克风权限才能开始翻译',
  },

  // 设置
  settings: {
    title: '设置',
    language: '语言设置',
    uiLanguage: '界面语言',
    theme: '主题',
    audio: '音频设置',
    account: '账户设置',
    privacy: '隐私设置',
    about: '关于应用',
    version: '版本信息',
    description: '配置应用程序选项',
    profile: '个人资料',
    security: '安全设置',
    dangerZone: '危险区域',
    pleaseLogin: '请先登录以查看账户设置',
  },

  // 个人资料设置
  profile: {
    displayName: '显示名称',
    email: '邮箱地址',
    emailNotifications: '邮件通知',
    emailNotificationsDesc: '接收重要更新和通知邮件',
    memberSince: '注册时间',
    enterName: '请输入姓名',
    nameRequired: '姓名不能为空',
    updateSuccess: '资料更新成功',
    updateFailed: '资料更新失败',
    avatarUploadComingSoon: '头像上传功能即将推出',
  },

  // 安全设置
  security: {
    twoFactorAuth: '双因素认证',
    mfaDescription: '为您的账户添加额外的安全保护层',
    enableMfa: '启用双因素认证',
    disable: '禁用',
    enable: '启用',
    enabled: '已启用',
    mfaEnabled: '双因素认证已启用',
    mfaDisabled: '双因素认证已禁用',
    mfaUpdateFailed: '双因素认证设置失败',
    socialLoginMfaWarning: '社交登录账户不支持双因素认证',
    socialLoginMfaWarningDesc: '使用社交登录的账户由第三方提供商管理安全设置',
    password: '密码',
    changePassword: '修改密码',
    passwordDescription: '定期更新密码以保护账户安全',
    currentPassword: '当前密码',
    newPassword: '新密码',
    confirmPassword: '确认新密码',
    updatePassword: '更新密码',
    passwordChanged: '密码修改成功',
    passwordChangeFailed: '密码修改失败',
    passwordMismatch: '两次输入的密码不一致',
    passwordTooShort: '密码长度至少8位',
    loginHistory: '登录历史',
    lastLogin: '最后登录',
    never: '从未',
    accountCreated: '账户创建时间',
    loginMethod: '登录方式',
    socialLogin: '社交登录',
    socialLoginConnected: '已连接 {provider} 账户',
    socialLoginNote: '注意',
    socialLoginNoteDesc: '您正在使用 {provider} 社交登录，密码和安全设置由 {provider} 管理',
    mfaNotAvailable: '双因素认证不可用',
    mfaNotAvailableTitle: '社交登录账户不支持此功能',
    mfaNotAvailableDesc: '使用社交登录的账户安全设置由第三方提供商管理，无法在此处启用双因素认证',
    mfaModalDescription: '启用双因素认证以增强账户安全性',
    mfaWillProvide: '双因素认证将为您提供：',
    mfaBenefit1: '额外的账户安全保护',
    mfaBenefit2: '防止未授权访问',
    mfaBenefit3: '符合安全最佳实践',
    mfaWarning: '启用后，每次登录都需要提供验证码',
    logoutAllDevices: '登出所有设备',
    logoutAllDevicesDesc: '强制登出所有已登录的设备和会话',
    logout: '登出',
    delete: '删除',
    deleteAccount: '删除账户',
    deleteAccountDesc: '永久删除您的账户和所有相关数据',
    confirmLogout: '确认登出',
    confirmLogoutDesc: '这将登出您在所有设备上的会话，您需要重新登录',
    confirmDelete: '确认删除账户',
    confirmDeleteDesc: '此操作不可撤销，将永久删除您的账户和所有数据',
    deleteWarning: '警告：此操作不可撤销',
    deleteWarning1: '所有个人数据将被永久删除',
    deleteWarning2: '对话历史和设置将丢失',
    deleteWarning3: '无法恢复已删除的账户',
    deleteConfirmLabel: '请输入以下文本以确认删除',
    logoutSuccess: '已成功登出所有设备',
    logoutFailed: '登出失败，请重试',
    accountDeleted: '账户已成功删除',
    accountDeleteFailed: '账户删除失败，请重试',
    deleteConfirmError: '请输入正确的确认文本',
  },

  // 错误信息
  errors: {
    networkError: '网络连接错误',
    serviceUnavailable: '服务暂时不可用',
    translationFailed: '翻译失败，请重试',
    audioError: '音频设备错误',
    permissionError: '权限错误',
    unknownError: '未知错误',
    tryAgain: '请重试',
  },

  // 首页
  home: {
    title: '闪译猫 AI 同传',
    subtitle: '丝滑的实时翻译',
    description: '支持多种语言的实时语音翻译，让沟通无障碍',
    getStarted: '开始使用',
    features: {
      realTime: '实时翻译',
      multiLanguage: '多语言支持',
      highQuality: '高质量翻译',
    },
  },

  // 免费试用
  freeTrial: {
    title: '试用中',
    description: '体验完整功能请登录',
    tokensRemaining: '剩余额度',
    timeRemaining: '剩余时间',
    upgradeNow: '立即升级',
    trialExpired: '试用已过期',
    trialEnded: '试用已结束',
    trialUnavailable: '免费试用不可用',
    getOfficialAccount: '获取正式账户',
    loginForMore: '登录获取更多',
    expired: '已过期',
    tokensExhausted: '试用 Token 已用完',
    notEligible: '不符合试用资格',
    errorMessage: '错误信息',
    reason: '原因',
    solution: '解决方案',
    registerLogin: '注册登录获取正式账户，享受完整功能',
    immediateRegister: '立即注册登录',
    conversationsNotSaved: '对话不会保存',
    experienceOnly: '仅用于体验',
    loginForPermanentSave: '登录后永久保存',
    hours: '小时',
    minutes: '分钟',
    unlimited: '无限制',
    freeQuota: '免费额度',
    tokens: 'Tokens',
    pro: 'Pro',

    // Banner specific texts
    betaStage: '内测阶段',
    betaInProgress: '内测阶段中',
    productInBeta: '产品内测中...',
    joinWaitlist: '加入waitlist',
    joinWaitlistFull: '加入 Waitlist',
    welcomeJoinWaitlist: '欢迎加入 Waitlist 获得优先体验机会！',
    freeTrialActive: '免费试用中',
    freeTrialNotAvailable: '免费试用不可用',
    trialEndedShort: '试用已结束',
    freeTrialEndedFull: '免费试用已结束',
    tokensUsedUp: 'Token已用完',
    tokensUsedUpDesc: '您的免费试用额度已全部使用',
    trialExpiredDesc: '免费试用时间已结束',
    registerForMore: '立即注册登录',
    registerForMoreDesc: '获取更多Token，永久保存对话记录',
    fullFeatureExperience: '完整功能体验',
    fullFeatureExperienceDesc: '解锁所有高级翻译功能',
    remainingTokens: '剩余Token',
    remainingTime: '剩余时间',
    freeTrialNotice: '免费试用说明',
    conversationsNotSavedDesc: '退出翻译页面后，对话记录将丢失',
    experienceOnlyDesc: '您可以体验实时翻译功能的完整效果',
    loginForPermanentSaveDesc: '注册登录后，所有对话记录将永久保存到您的账户',
    deviceUsedTrialExpired: '您的设备已经使用过免费试用，且试用已过期',
    deviceUsedTrialExhausted: '您的设备已经使用过免费试用，token已用完',
  },

  // 身份验证
  auth: {
    // 通用
    login: '登录',
    logout: '退出',
    register: '注册',
    email: '邮箱',
    callback: '认证回调',
    password: '密码',
    forgotPassword: '忘记密码',
    rememberMe: '记住我',
    loginSuccess: '登录成功',
    loginError: '登录失败',
    logoutSuccess: '退出成功',

    // 登录表单
    welcomeBack: '欢迎回来',
    loginWithSocialOrEmail: '使用社交账户或邮箱登录',
    emailPlaceholder: 'm@example.com',
    forgotPasswordLink: '忘记密码？',
    loggingIn: '登录中...',
    dontHaveAccount: '还没有账户？',
    signUp: '注册',

    // 注册表单
    createAccount: '创建账户',
    verifyYourEmail: '验证您的邮箱',
    signUpWithSocialOrEmail: '使用 Apple 或 Google 账户注册',
    enterVerificationCode: '输入发送到您邮箱的验证码',
    fullName: '姓名',
    fullNamePlaceholder: '您的姓名',
    passwordPlaceholder: '至少8位字符',
    confirmPassword: '确认密码',
    confirmPasswordPlaceholder: '确认您的密码',
    invitationCode: '邀请码',
    invitationCodeRequired: '邀请码',
    invitationCodePlaceholder: '输入4位邀请码',
    invitationCodeDescription: '来自邀请邮件的区分大小写的4位字符码',
    verificationCode: '验证码',
    verificationCodePlaceholder: '6位验证码',
    codeSentTo: '验证码已发送至 {email}',
    resendVerificationCode: '重新发送验证码',
    sendVerificationCode: '发送验证码',
    createAccountButton: '创建账户',
    sendingCode: '发送验证码中...',
    creatingAccount: '创建账户中...',
    alreadyHaveAccount: '已有账户？',
    signIn: '登录',
    backToPreviousStep: '返回上一步',

    // 忘记密码表单
    resetPassword: '重置密码',
    setNewPassword: '设置新密码',
    enterEmailForReset: '输入您的邮箱地址，我们将发送重置码给您',
    newPassword: '新密码',
    newPasswordPlaceholder: '至少8位字符',
    confirmNewPassword: '确认新密码',
    confirmNewPasswordPlaceholder: '确认您的新密码',
    sendingCodeLoading: '发送验证码中...',
    verifying: '验证中...',
    resettingPassword: '重置密码中...',
    verifyCode: '验证码',
    resetPasswordButton: '重置密码',
    rememberPassword: '想起密码了？',
    backToLogin: '返回登录',

    // MFA 双因素认证
    verificationCodeMFA: '验证码',
    sendCode: '发送验证码',
    sending: '发送中...',
    resendCode: '重新发送验证码',
    clickSendCodeDescription: '点击"发送验证码"通过邮件接收验证码',

    // 社交登录
    orContinueWith: '或继续使用',
    orContinueWithEmail: '或使用邮箱继续',
    apple: 'Apple',
    google: 'Google',

    // 错误消息
    pleaseEnterEmail: '请输入邮箱地址',
    pleaseEnterEmailFirst: '请先输入邮箱地址',
    pleaseEnterVerificationCode: '请输入验证码',
    passwordsDoNotMatch: '两次输入的密码不一致',
    passwordTooShort: '密码长度至少8位',
    invitationCodeRequiredError: '内测期间需要邀请码',
    failedToSendCode: '发送验证码失败',
    invalidVerificationCode: '验证码无效',
    passwordResetFailed: '密码重置失败',
    registrationFailed: '注册失败，请重试',
    mfaRequired: '请输入双因素认证码',
    userDoesNotExist: '用户不存在',
    invalidCredentials: '邮箱或密码错误',

    // 成功消息
    verificationCodeSent: '验证码已发送到您的邮箱',
    passwordResetCodeSent: '密码重置码已发送到您的邮箱',
    codeVerified: '验证码验证成功，请设置新密码',
    passwordResetSuccessful: '密码重置成功！请使用新密码登录',
    registrationSuccessful: '注册成功！正在跳转...',
    mfaCodeSent: 'MFA验证码已发送到您的邮箱',

    // 法律条款
    termsAndPrivacy: '点击继续即表示您同意我们的',
    termsOfService: '服务条款',
    and: '和',
    privacyPolicy: '隐私政策',
  },

  // 聊天/对话
  chat: {
    conversations: '对话',
    noConversations: '暂无对话记录',
    loadingConversations: '加载对话中...',
    conversationHistory: '对话历史',
    backToList: '返回列表',
    totalTurns: '共 {count} 条对话',
    loadedTurns: '已加载 {count} 条',
    noContent: '此对话暂无内容',
    loadingConversation: '加载对话中...',
    sessionTitle: '会话标题',
    createdAt: '创建时间',
    loadMore: '加载更多',
    remainingTurns: '{count} 条剩余',
    allLoaded: '已加载全部',
    allLoadedMessage: '已加载全部 {count} 条对话',
    browseHistory: '浏览和查看历史对话记录',
    totalSessions: '共 {count} 个会话',
    startNewConversation: '开始一段新对话吧',
    deleteSession: '删除会话',
    confirmDeleteSession: '确认删除会话',
    deleteSessionWarning: '确定要删除会话 "{title}" 吗？\n此操作不可撤销，将永久删除该会话及其所有对话记录。',
    confirmDelete: '确认删除',
    turnsCount: '{count} 条对话',
    preview: '预览 {count} 条',
  },

  // 购买/支付
  purchase: {
    title: '购买',
    buyTokens: '购买额度',
    orderHistory: '订单记录',
    noOrders: '暂无订单记录',
    loadingOrders: '加载订单中...',
    paymentMethod: '支付方式',
    amount: '金额',
    status: '状态',
    date: '日期',
    processing: '处理中',
    completed: '已完成',
    failed: '失败',

    // 购买页面
    subscriptionPlans: '订阅计划',
    subscriptionDescription: '选择月度订阅，无限制使用所有功能并享受优质支持',
    subscribeNow: '立即订阅',
    tokenPacks: 'Token 包',
    tokenPacksDescription: '购买 Token 包，按需付费，无月度承诺',
    buyNow: '立即购买',
    oneTimePayment: '一次性付款',
    popular: '热门',
    recommended: '推荐',
    perMonth: '/月',
    tokens: 'Tokens',
    features: '功能特性：',

    // Token 包特性
    tokensIncluded: '个 Token',
    noExpirationDate: '永不过期',
    payOnceUseAnytime: '一次付费，随时使用',
    perfectForOccasionalUsers: '偶尔使用的完美选择',

    // 支付方式选择
    selectPaymentMethod: '选择支付方式',
    selectPaymentDescription: '选择您偏好的支付方式以安全完成购买。',
    alipayName: 'Alipay (支付宝)',
    alipayDescription: '通过支付宝二维码安全支付 - 快速可靠',
    polarName: 'Polar.sh',
    polarDescription: '国际支付平台 - 全球可用',
    creatingOrder: '创建订单中...',
    cancel: '取消',

    // 支付处理
    scanToPay: '扫码支付',
    completePayment: '完成支付',
    alipayPaymentDescription: '使用支付宝应用扫描二维码以安全完成支付。',
    polarPaymentDescription: '在打开的窗口中完成支付。我们会自动检测支付完成状态。',
    paymentWindowOpened: '支付窗口已打开',
    completePaymentInNewWindow: '在新窗口中完成支付',
    openInBrowser: '在浏览器中打开',
    reopenPaymentWindow: '重新打开支付窗口',
    checkingStatus: '检查状态中...',
    checkPaymentStatus: '检查支付状态',
    close: '关闭',
    orderId: '订单ID',

    // 支付状态
    waitingForPayment: '等待支付中...',
    paymentReceived: '已收到付款，处理中...',
    paymentSuccessful: '支付成功！',
    paymentFailed: '支付失败，请重试。',
    paymentExpired: '支付已过期，请创建新订单。',
  },

  // 调试/开发
  debug: {
    debugPanel: '调试面板',
    testEvents: '测试事件',
    parseEvents: '解析事件',
    clearEvents: '清除测试事件',
    clearAll: '清空所有',
    eventData: '事件 JSON 数据',
    parseError: '解析错误',
    testAndParse: '测试并解析',
    inputPlaceholder: '输入 SessionEvent[] JSON 数据...',
  },

  // 管理后台
  admin: {
    title: '管理后台',
  },

  // UI组件
  ui: {
    swap: '交换',
    siderbarTitle: '主要功能',
    selectLanguageDescription: '选择应用界面显示语言',
    translationLanguageSettings: '翻译语言设置',
    comingSoon: '功能即将推出...',
    audioSettingsDescription: '配置麦克风和扬声器设置',
    accountSettingsDescription: '管理用户账户和个人资料',
    microphoneAccessDenied: '麦克风权限被禁用',
    microphoneInstructions: '请在浏览器设置中允许访问麦克风，然后刷新页面',
    refreshPage: '刷新页面',
    openSettings: '设置',
    dismiss: '关闭',
    microphonePermissionSteps: '请按以下步骤开启麦克风权限：',
    chromeInstructions: '点击地址栏左侧的锁图标 → 选择"网站设置" → 允许麦克风访问',
    firefoxInstructions: '点击地址栏左侧的盾牌图标 → 选择"权限" → 允许麦克风访问',
    safariInstructions: '点击Safari菜单 → 偏好设置 → 网站 → 麦克风 → 允许此网站访问',
    genericInstructions: '请在浏览器设置中允许此网站访问麦克风',
    settingsCompleteRefresh: '设置完成后请刷新页面。',
    iosPermissionTitle: 'iOS 麦克风权限设置',
    iosPermissionDescription: 'iOS 系统需要您每次访问时重新授权麦克风权限。请按以下步骤操作：',
    iosPermissionTip: '这是 iOS 系统的安全特性，每次新会话都需要重新授权。为了更好的体验，建议将网站添加到主屏幕作为 Web App 使用。',
    iosHelp: 'iOS 帮助',
    retryPermission: '重新尝试',
    openSystemSettings: '打开设置',
  },

  // 对话组件
  conversation: {
    noContent: '暂无对话内容',
    startSpeaking: '开始说话，自动翻译',
    autoTranslate: '自动翻译',
    turnsCount: '{count} 条对话',
    tokens: 'tokens',
    totalCost: '本次费用',
    translation: 'Translation',
    userA: 'User A',
    userB: 'User B',
    cancelled: 'Cancelled',
    translationIncomplete: '翻译未完成',
  },
};
